import COLORS from "../design-tokens/colors";

/**
 * @typedef {Object} Theme An object of CSS classes to be applied for the theme
 * @property {string} backgroundColor
 * @property {string} hoverBackgroundColor
 * @property {string} textColor
 * @property {string} hoverTextColor
 * @property {string} translucentTextColor
 * @property {string} borderColor
 * @property {string} ruleColor
 * @property {string} componentColorTheme
 * @property {string} [focusTextColor]
 * @property {string} [focusOutlineColor]
 * @property {string} [textGradientValue]
 */

/**
 * @typedef {keyof typeof STANDARD_THEMES} StandardThemeName

 * @typedef {keyof typeof PATTERN_THEMES} PatternThemeName

 * @typedef {keyof typeof DEM_FORUM_THEMES} DemForumThemeName

* @typedef {StandardThemeName | PatternThemeName | DemForumThemeName} ThemeName
 */

/**
 * @typedef {Object.<StandardThemeName, Theme>} ThemeTable1
 * @description An object that maps theme names to their corresponding theme configurations.
 */
export const STANDARD_THEMES = {
  transparent: {
    backgroundColor: "bg-transparent",
    hoverBackgroundColor: "hover:bg-transparent",
    textColor: "text-transparent",
    translucentTextColor: "text-transparent",
    hoverTextColor: "hover:text-transparent",
    borderColor: "border-transparent",
    ruleColor: "bg-transparent",
    focusOutlineColor: "focus-visible:outline-transparent",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.transparent,
    textColorValue: COLORS.transparent
  },
  black: {
    backgroundColor: "bg-black",
    hoverBackgroundColor: "hover:bg-white",
    textColor: "text-white",
    translucentTextColor: "text-white-60",
    hoverTextColor: "hover:text-cobalt-blue",
    borderColor: "border-black",
    ruleColor: "bg-white",
    focusOutlineColor: "focus-visible:outline-white",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.black,
    textColorValue: COLORS.white,
    textGradientValue: "bg-blue-green-animated-text"
  },
  white: {
    backgroundColor: "bg-white",
    hoverBackgroundColor: "hover:bg-black",
    borderColor: "border-white",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "hover:text-cobalt-blue",
    ruleColor: "bg-black",
    focusTextColor: "focus-visible:text-cobalt-blue",
    focusOutlineColor: "focus-visible:outline-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.white,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  darkBlue: {
    backgroundColor: "bg-dark-blue",
    hoverBackgroundColor: "",
    textColor: "text-white",
    translucentTextColor: "text-white-60",
    hoverTextColor: "",
    borderColor: "border-dark-blue",
    ruleColor: "bg-white",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.darkBlue,
    textColorValue: COLORS.white,
    textGradientValue: "bg-blue-green-animated-text"
  },
  cobaltBlue: {
    backgroundColor: "bg-cobalt-blue",
    hoverBackgroundColor: "",
    textColor: "text-white",
    translucentTextColor: "text-white",
    hoverTextColor: "hover:text-white",
    focusOutlineColor: "focus-visible:outline-white",
    borderColor: "border-cobalt-blue",
    ruleColor: "bg-white",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.cobaltBlue,
    textColorValue: COLORS.white,
    textGradientValue: "bg-blue-green-animated-text"
  },
  skyBlue: {
    backgroundColor: "bg-sky-blue",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-sky-blue",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.skyBlue,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  lightBlue: {
    backgroundColor: "bg-light-blue",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-light-blue",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.lightBlue,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  lightGrey: {
    backgroundColor: "bg-light-grey",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "hover:text-cobalt-blue",
    borderColor: "border-light-grey",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    focusTextColor: "focus-visible:text-cobalt-blue",
    focusOutlineColor: "focus-visible:outline-black",
    backgroundColorValue: COLORS.lightGrey,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  red: {
    backgroundColor: "bg-red",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-75",
    hoverTextColor: "",
    borderColor: "border-red",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.red,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  orange: {
    backgroundColor: "bg-orange",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-75",
    hoverTextColor: "",
    borderColor: "border-orange",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.orange,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  yellow: {
    backgroundColor: "bg-yellow",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-yellow",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.yellow,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  green: {
    backgroundColor: "bg-green",
    hoverBackgroundColor: "",
    textColor: "text-white",
    translucentTextColor: "text-white-80",
    hoverTextColor: "",
    borderColor: "border-green",
    ruleColor: "bg-white",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.green,
    textColorValue: COLORS.white,
    textGradientValue: "bg-blue-green-animated-text"
  },
  pink: {
    backgroundColor: "bg-pink",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-pink",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.pink,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  lightOrange: {
    backgroundColor: "bg-light-orange",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-light-orange",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.lightOrange,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  lightYellow: {
    backgroundColor: "bg-light-yellow",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-light-yellow",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.lightYellow,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  lightGreen: {
    backgroundColor: "bg-light-green",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-light-green",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.lightGreen,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  goaPurple: {
    backgroundColor: "bg-goa-purple",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black",
    hoverTextColor: "",
    borderColor: "border-goa-purple",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.goaPurple,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  goaPink: {
    backgroundColor: "bg-goa-pink",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-80",
    hoverTextColor: "",
    borderColor: "border-goa-pink",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.goaPink,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  teal: {
    backgroundColor: "bg-goa-teal",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-goa-teal",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.goaTeal,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  darkTeal: {
    backgroundColor: "bg-goa-dark-teal",
    hoverBackgroundColor: "",
    textColor: "text-white",
    translucentTextColor: "text-white",
    hoverTextColor: "",
    borderColor: "border-goa-dark-teal",
    ruleColor: "bg-white",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.goaDarkTeal,
    textColorValue: COLORS.white,
    textGradientValue: "bg-blue-green-animated-text"
  },
  futurePink: {
    backgroundColor: "bg-future-pink",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-future-pink",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.futurePink,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  futureGreen: {
    backgroundColor: "bg-future-green",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black",
    hoverTextColor: "",
    borderColor: "border-future-green",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.futureGreen,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  scholarsMaroon: {
    backgroundColor: "bg-scholars-maroon",
    hoverBackgroundColor: "",
    textColor: "text-white",
    translucentTextColor: "text-white-80",
    hoverTextColor: "",
    borderColor: "border-scholars-maroon",
    ruleColor: "bg-white",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.scholarsMaroon,
    textColorValue: COLORS.white,
    textGradientValue: "bg-blue-green-animated-text"
  },
  sand: {
    backgroundColor: "bg-scholars-sand",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-scholars-sand",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.scholarsSand,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  }
};

/**
 * @typedef {Object.<PatternThemeName, Theme>} ThemeTable2
 * @description An object that maps theme names to their corresponding theme configurations.
 */
export const PATTERN_THEMES = {
  skyBlueSpark: {
    ...STANDARD_THEMES.skyBlue,
    pattern: "spark"
  },
  darkBlueRadiate: {
    ...STANDARD_THEMES.darkBlue,
    pattern: "radiate"
  },
  yellowAmplify: {
    ...STANDARD_THEMES.yellow,
    pattern: "amplify"
  }
};

/**
 * @typedef {Object.<DemForumThemeName, Theme>} ThemeTable3
 * hard-coded one-off themes
 */
export const DEM_FORUM_THEMES = {
  "Dem Forum Light Green Sky Blue": {
    backgroundColor: "bg-gradient-radial from-light-green from-49% to-sky-blue to-94%",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-black",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.skyBlue,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  "Dem Forum Orange Cobalt Blue": {
    backgroundColor: "bg-gradient-radial from-orange from-49% to-cobalt-blue to-94%",
    hoverBackgroundColor: "",
    textColor: "text-white",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-black",
    ruleColor: "bg-white",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.cobaltBlue,
    textColorValue: COLORS.black,
    textGradientValue: "bg-blue-green-animated-text"
  },
  "Dem Forum Orange Sky Blue": {
    backgroundColor: "bg-gradient-radial from-orange from-49% to-sky-blue to-94%",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-black",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.skyBlue,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  "Dem Forum Yellow Cobalt Blue": {
    backgroundColor: "bg-gradient-radial from-yellow from-49% to-cobalt-blue to-94%",
    hoverBackgroundColor: "",
    textColor: "text-white",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-black",
    ruleColor: "bg-white",
    componentColorTheme: "black",
    backgroundColorValue: COLORS.cobaltBlue,
    textColorValue: COLORS.black,
    textGradientValue: "bg-blue-green-animated-text"
  },
  "Dem Forum Yellow Orange": {
    backgroundColor: "bg-gradient-radial from-yellow from-49% to-orange to-94%",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-black",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.orange,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  },
  "Dem Forum Yellow Sky Blue": {
    backgroundColor: "bg-gradient-radial from-yellow from-49% to-sky-blue to-94%",
    hoverBackgroundColor: "",
    textColor: "text-black",
    translucentTextColor: "text-black-60",
    hoverTextColor: "",
    borderColor: "border-black",
    ruleColor: "bg-black",
    componentColorTheme: "white",
    backgroundColorValue: COLORS.skyBlue,
    textColorValue: COLORS.black,
    textGradientValue: "bg-dark-blue-animated-text"
  }
};

/**
 * @typedef {Object.<ThemeName, Theme>} ThemeTable
 * @description An object that maps theme names to their corresponding theme configurations.
 */
export const THEMES = {
  ...STANDARD_THEMES,
  ...PATTERN_THEMES,
  ...DEM_FORUM_THEMES
};

/**
 * @type {StandardThemeName[]}
 */
export const STANDARD_THEME_NAMES = Object.keys(STANDARD_THEMES);

/**
 * @type {PatternThemeName[]}
 */
export const PATTERN_THEME_NAMES = Object.keys(PATTERN_THEMES);

/**
 * @type {DemForumThemeName[]}
 */
export const DEM_FORUM_THEME_NAMES = Object.keys(DEM_FORUM_THEMES);

/**
 * @type {ThemeName[]}
 */
export const THEME_NAMES = Object.keys(THEMES);

/**
 *
 * @param {ThemeName} themeName
 * @returns {Theme | undefined}
 */
export const getTheme = (themeName) => {
  if (!themeName) {
    return;
  }

  if (!THEME_NAMES.includes(themeName)) {
    // eslint-disable-next-line no-console
    console.error(
      `[ERROR] "${themeName}" is not a supported theme value. Must be one of: ${THEME_NAMES.join(
        " "
      )}`
    );
  }

  return THEMES[themeName];
};
